import { useLocation, useParams, useSearch } from '@tanstack/react-router';
import { useMemo } from 'react';

import type { ConditionMap } from '../conditions';
import type { BaseLinkRoute, BaseRoute, PrevNextResult } from './types';

import { filterRoutes, findHeaderTabs, findPrevNext, flattenLinkRoutes, flattenSidebarRoutes } from './utils';

// ============================================================================
// Navigation Hooks Factory
// ============================================================================

/**
 * Creates typed navigation hooks for an app's navigation system.
 * This factory produces hooks that use the app's navigation context and routes.
 *
 * Now supports named conditions via the optional useNavigationConditions hook.
 * If provided, routes can use string condition names that are resolved from
 * the conditions map.
 *
 * @example
 * // In your app's navigation hooks file:
 * import { createNavigationHooks } from '@connected/core';
 * import { navigationRoutes, useNavigationContext, useNavigationConditions } from './navigation-config';
 *
 * export const {
 *     useFilteredRoutes,
 *     useFlattenedRoutes,
 *     useFlattenedSidebarRoutes,
 *     useHeaderNavigation,
 *     usePrevNextNavigation,
 * } = createNavigationHooks(useNavigationContext, navigationRoutes, useNavigationConditions);
 */
export function createNavigationHooks<TContext, TConditionName extends string = string>(
    useNavigationContext: () => TContext,
    routes: BaseRoute<TContext, TConditionName>[],
    useNavigationConditions?: () => ConditionMap<TContext>,
) {
    /**
     * Returns filtered navigation routes based on context.
     * Supports both inline function conditions and named string conditions.
     */
    function useFilteredRoutes(): BaseRoute<TContext, TConditionName>[] {
        const context = useNavigationContext();
        const conditions = useNavigationConditions?.() ?? {};

        return useMemo(
            () => filterRoutes(routes, context, conditions),
            [context, conditions],
        );
    }

    /**
     * Returns only the top-level routes for sidebar display (excludes nested tab routes).
     */
    function useSidebarRoutes(): BaseRoute<TContext, TConditionName>[] {
        return useFilteredRoutes();
    }

    /**
     * Returns ordered flat list of all visible LinkRoutes for prev/next traversal.
     */
    function useFlattenedRoutes(): BaseLinkRoute<TContext, TConditionName>[] {
        const filteredRoutes = useFilteredRoutes();

        return useMemo(
            () => flattenLinkRoutes(filteredRoutes),
            [filteredRoutes],
        );
    }

    /**
     * Returns ordered flat list of sidebar-only routes (top-level LinkRoutes without nested children).
     */
    function useFlattenedSidebarRoutes(): BaseLinkRoute<TContext, TConditionName>[] {
        const filteredRoutes = useFilteredRoutes();

        return useMemo(
            () => flattenSidebarRoutes(filteredRoutes),
            [filteredRoutes],
        );
    }

    /**
     * Finds the parent LinkRoute that contains nested routes (tabs) for the current path.
     * Returns the nested routes if found, or null if not on a tabbed page.
     */
    function useHeaderNavigation(): BaseLinkRoute<TContext, TConditionName>[] | null {
        const location = useLocation();
        const params = useParams({ strict: false });
        const filteredRoutes = useFilteredRoutes();

        return useMemo(() => {
            const currentPath = location.pathname;
            return findHeaderTabs(filteredRoutes, currentPath, params);
        }, [filteredRoutes, location.pathname, params]);
    }

    /**
     * Returns prev/next routes for current location.
     * Handles nested tab navigation with exitTo support.
     */
    function usePrevNextNavigation(): PrevNextResult<TContext, TConditionName> {
        const location = useLocation();
        const params = useParams({ strict: false });
        const flatRoutes = useFlattenedRoutes();

        return useMemo(() => {
            const currentPath = location.pathname;
            return findPrevNext(flatRoutes, currentPath, params);
        }, [flatRoutes, location.pathname, params]);
    }

    return {
        useFilteredRoutes,
        useSidebarRoutes,
        useFlattenedRoutes,
        useFlattenedSidebarRoutes,
        useHeaderNavigation,
        usePrevNextNavigation,
    };
}

// ============================================================================
// Standalone Hooks
// ============================================================================

/**
 * Hook to get current route params and search for use in Link components.
 * This is a convenience wrapper around TanStack Router hooks.
 */
export function useRouteContext() {
    const params = useParams({ strict: false });
    const search = useSearch({ strict: false });

    return { params, search };
}
